// Phone Call button (Task 13.6).
//
// Renders a "Call Restaurant" / "Call NGO" button (Req 18.4). Activating it
// opens the device's native dialer via a tel: link pre-populated with the
// party's Phone Number (Req 18.5). If no dialer/telephony is available, an
// error is shown and the number is revealed for manual dialing (Req 18.6).
// When the party has no Phone Number on record (server withholds it or none
// exists), the button is disabled (Req 18.7). Phone visibility is gated
// server-side — this component only displays what the backend returns.

import { useEffect, useState } from "react";
import * as api from "../lib/api";

export function CallButton({
  targetUid,
  label,
  phoneNumber,
}: {
  targetUid: string;
  /** e.g. "Call Restaurant" or "Call NGO" (Req 18.4). */
  label: string;
  /** Provide a known number to skip the lookup (tests/optimization). */
  phoneNumber?: string | null;
}): JSX.Element {
  const [phone, setPhone] = useState<string | null | undefined>(phoneNumber);
  const [loading, setLoading] = useState(phoneNumber === undefined);
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    if (phoneNumber !== undefined) {
      setPhone(phoneNumber);
      return;
    }
    let active = true;
    api
      .getPhoneNumber(targetUid)
      .then((p) => {
        if (active) setPhone(p);
      })
      .catch(() => {
        if (active) setPhone(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [targetUid, phoneNumber]);

  function handleCall(): void {
    if (!phone) return;
    try {
      // Opening a tel: URL hands off to the native dialer (Req 18.5).
      window.location.href = `tel:${phone}`;
    } catch {
      // No telephony capability: reveal the number for manual dialing
      // (Req 18.6).
      setFallback(true);
    }
  }

  const disabled = loading || !phone;

  return (
    <span>
      <button
        type="button"
        className="sb-btn sb-btn--secondary"
        onClick={handleCall}
        disabled={disabled}
      >
        {label}
      </button>
      {fallback && phone && (
        <span className="sb-field__error" role="alert">
          Calling is unavailable. Dial {phone} manually.
        </span>
      )}
    </span>
  );
}
